import './Wootric.css';

import { ErrorBoundary, useCurrentAgreement, useCurrentLocale } from '@economic/react-app';
import { isToday } from 'date-fns';
import React from 'react';
import { WootricConfigurationModel } from '../../../types/internal-api';
import useCachedQuery from '../hooks/useCachedQuery';
import useLocalStorage from '../hooks/useLocalStorage';

interface WootricSettings {
    external_id?: string;
    product_name?: string;
    created_at?: number;
    email?: string;
    account_token?: string;
    language?: string;
    [key: string]: any;
    properties?: {
        subscriptionType?: string;
        agreementSignup_date?: number;
        userType?: string;
        isAdmin?: string;
        addOnModules_amount?: number;
        appsInstalled_amount?: number;
        hasInventory?: string;
        hasProjects?: string;
        hasScanning?: string;
        hasSmartInbox?: string;
        hasSpotFactoring?: string;
        hasSmartBank?: string;
        hasElectronicInvoiceReception?: string;
        hasDocRequest?: string;
        [key: string]: any;
    };
}

declare global {
    interface Window {
        // Overwrites sampling settings and forces the server to return true to all survey requests.
        wootric_survey_immediately: boolean;
        // Disables the cookie writing after a survey is taken effectively disabling any client side mechanisms to prevent multiple surveys from being rendered.
        wootric_no_surveyed_cookie: boolean;
        wootricSettings: WootricSettings;
        WootricSurvey: {
            run: (settings: WootricSettings) => void;
            stop: () => void;
            version: () => string;
        };
    }
}

const hasWootricModalBeenShownToday = (wootricModalLastShownDate: Date | undefined) =>
    !!wootricModalLastShownDate && isToday(wootricModalLastShownDate);

const convertBoolToYesNo = (value: boolean) => (value ? 'yes' : 'no');

function WootricSurveyModal() {
    const [wootricModalLastShownDate, setWootricModalLastShownDate] = useLocalStorage<Date | undefined>(
        'WootricModalLastShownDate'
    );

    const { data: wootricConfig } = useCachedQuery<WootricConfigurationModel>('wootric/config', null, {
        skip: hasWootricModalBeenShownToday(wootricModalLastShownDate),
    });

    const currentLocale = useCurrentLocale();
    const currentAgreement = useCurrentAgreement();

    React.useEffect(() => {
        if (hasWootricModalBeenShownToday(wootricModalLastShownDate) || !wootricConfig || !currentLocale || !currentAgreement) {
            return;
        }

        if (!wootricConfig.canParticipateInSurvey) {
            return;
        }

        window.wootricSettings = {
            external_id: wootricConfig.externalId,
            product_name: wootricConfig.productName,
            created_at: wootricConfig.userSignUpDateUnixEpoch,
            email: wootricConfig.userEmail,
            account_token: wootricConfig.accountToken,
            language: currentLocale.languageCode,
            properties: {
                agreementSignup_date: wootricConfig.agreementSignupDateUnixEpoch,
                userType: wootricConfig.userType,
                isAdmin: convertBoolToYesNo(wootricConfig.isAdminUser),
                appsInstalled_amount: wootricConfig.installedAppsCount,
                subscriptionType: currentAgreement.subscription?.name,
                addOnModules_amount: Object.keys(currentAgreement.modules).length,
                hasInventory: convertBoolToYesNo(currentAgreement.modules.inventory),
                hasProjects: convertBoolToYesNo(currentAgreement.modules.project),
                hasScanning: convertBoolToYesNo(currentAgreement.modules.scanning),
                hasSmartInbox: convertBoolToYesNo(currentAgreement.modules.smartInbox),
                hasSpotFactoring: convertBoolToYesNo(currentAgreement.modules.moneyflow),
                hasSmartBank: convertBoolToYesNo(currentAgreement.modules.smartBank),
                hasElectronicInvoiceReception: convertBoolToYesNo(currentAgreement.modules.electronicDocumentReception),
                hasDocRequest: convertBoolToYesNo(currentAgreement.modules.documentRequest),
            },
        };

        const beaconScriptElement = document.createElement('script');
        beaconScriptElement.type = 'text/javascript';
        beaconScriptElement.id = 'wootric-beacon';
        beaconScriptElement.async = true;

        beaconScriptElement.src = 'https://cdn.wootric.com/wootric-sdk.js';
        beaconScriptElement.onload = () => {
            window.WootricSurvey && window.WootricSurvey.run(window.wootricSettings);
            setWootricModalLastShownDate(new Date());
        };

        if (document.getElementById('wootric-beacon') == null) {
            document.body.appendChild(beaconScriptElement);
        }

        // Clean up
        return () => {
            window.WootricSurvey && window.WootricSurvey.stop();
        };
    }, [wootricModalLastShownDate, wootricConfig, currentLocale, currentAgreement]);

    return <></>;
}

function AnErrorOccured({ error: _ }: { error?: Error }) {
    // Do nothing. We do not want to show any UI to handle wootric failures.
    return <></>;
}

export function WootricSurveyModalWithErrorBoundary() {
    return (
        <ErrorBoundary component={<AnErrorOccured />}>
            {/* Wootric errors should not impact the rest of the app */}
            <WootricSurveyModal />
        </ErrorBoundary>
    );
}
